import {
  finishFederatedSignupSchema,
  CENTRAL_AUTH_PAGE_PATHS,
  ianaTimezoneSchema,
  parseValidationFailureDetails,
  type FinishFederatedSignupInput,
} from '@instanexus/contracts';
import {
  errorMessage,
  textPolicyMessage,
  useTermsAgreement,
  useTranslation,
} from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PhoneInput,
  TermsAgreementText,
  TextLink,
} from '@instanexus/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { CaptchaField } from '@/components/captcha-field';
import { useApiClient } from '@/hooks/use-api-client';
import { useCaptcha } from '@/hooks/use-captcha';
import { verifyPhonePath } from '@/lib/auth-paths';
import { finishCentralSignIn } from '@/lib/central-session';
import { restartFederatedRoundTrip } from '@/lib/federated-restart';
import { SIGN_UP_LANDING_REQUEST } from '@/lib/sign-in-request';

/** Not a declared `ErrorCode`; `errorMessage()` falls back to `errors:unexpected`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';
const EXPIRED = 'FEDERATED_SIGNUP_EXPIRED';

const formSchema = finishFederatedSignupSchema.omit({ captchaToken: true, timezone: true });

/** The contract minus the captcha; `acceptTerms` is held as a boolean (unchecked by default). */
type FederatedSignupFormValues = Omit<
  FinishFederatedSignupInput,
  'acceptTerms' | 'captchaToken' | 'timezone'
> & {
  acceptTerms: boolean;
};

/** Every path this form can attach a server field error to. */
const FORM_FIELDS = [
  'name',
  'tenantName',
  'phone',
  'acceptTerms',
] as const satisfies readonly (keyof FederatedSignupFormValues)[];

/** This browser's IANA zone, or `undefined` when it is not one the contract accepts. */
function resolveBrowserTimezone(): string | undefined {
  const parsed = ianaTimezoneSchema.safeParse(Intl.DateTimeFormat().resolvedOptions().timeZone);
  return parsed.success ? parsed.data : undefined;
}

/** An untouched optional field holds `''`; the same trimmed value is validated and sent. */
function normalizeValues(values: FederatedSignupFormValues): FederatedSignupFormValues {
  const phone = values.phone?.trim() ?? '';
  return {
    name: values.name.trim(),
    tenantName: values.tenantName.trim(),
    acceptTerms: values.acceptTerms,
    ...(phone.length > 0 ? { phone } : {}),
  };
}

function isBranch(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Rewrites every resolver message into this app's translated copy (docs/011). A field with
 * no specific message gets an empty one: the red `aria-invalid` border marks the box.
 */
function localizeErrors(
  errors: unknown,
  policyMessage: (code: string) => string | null,
  termsMessage: string,
): void {
  if (!isBranch(errors)) return;
  for (const [name, error] of Object.entries(errors)) {
    if (!isBranch(error)) continue;
    const original = error['message'];
    const policy =
      name === 'tenantName' && error['type'] === 'custom' && typeof original === 'string'
        ? policyMessage(original)
        : null;
    error['message'] = name === 'acceptTerms' ? termsMessage : (policy ?? '');
  }
}

/** Navigation seam: the page's only way out to another origin. */
function redirectTo(url: string): void {
  window.location.assign(url);
}

/**
 * The organization + Terms step of a provider (Google) sign-up on the central app.
 *
 * The pending sign-up is carried by the `inx_fed_signup` cookie, so a reload shows the same form.
 * The email is the provider-proven one and is never sent back; there is no password field.
 */
export function FederatedSignupPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const navigate = useNavigate();

  const pending = useQuery({
    queryKey: ['federation', 'pendingSignup'],
    queryFn: () => api.federation.pendingSignup(),
    retry: false,
  });

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const validationMessage = errorMessage(t, 'VALIDATION_FAILED');
  const agreement = useTermsAgreement();
  const termsRequiredMessage = agreement.requiredMessage;

  const resolver = useMemo<Resolver<FederatedSignupFormValues>>(() => {
    // The schema's `acceptTerms` is `z.literal(true)`; the form holds a boolean.
    const base = zodResolver(formSchema) as unknown as Resolver<FederatedSignupFormValues>;
    return async (values, context, options) => {
      const result = await base(normalizeValues(values), context, {
        criteriaMode: options.criteriaMode,
        fields: options.fields,
        shouldUseNativeValidation: options.shouldUseNativeValidation,
      });
      localizeErrors(
        result.errors,
        (code) => textPolicyMessage(t, 'custom', code),
        termsRequiredMessage,
      );
      return result;
    };
  }, [termsRequiredMessage, t]);

  const captcha = useCaptcha();

  const form = useForm<FederatedSignupFormValues>({
    resolver,
    defaultValues: { name: '', tenantName: '', phone: '', acceptTerms: false },
  });

  const email = pending.data?.email ?? '';

  const onSubmit = form.handleSubmit(async (values: FederatedSignupFormValues): Promise<void> => {
    setErrorCode(null);
    if (captcha.token === null) return;
    const captchaToken = captcha.token;
    const { acceptTerms: _checked, ...request } = normalizeValues(values);
    const timezone = resolveBrowserTimezone();

    try {
      const result = await api.federation.finishSignup({
        ...request,
        acceptTerms: true,
        captchaToken,
        ...(timezone === undefined ? {} : { timezone }),
      });
      if (result.phoneCodeSent) {
        await navigate(verifyPhonePath(email, request.phone ?? ''));
        return;
      }
      if (result.session === null) {
        await restartFederatedRoundTrip(
          api,
          pending.data?.provider ?? 'google',
          { kind: 'sign_in', search: 'client=admin', ...(email === '' ? {} : { loginHint: email }) },
          redirectTo,
        );
        return;
      }
      await finishCentralSignIn(
        api,
        SIGN_UP_LANDING_REQUEST,
        'client=admin',
        result.session.accessToken,
        { navigate: (to) => void navigate(to), redirectTo },
      );
    } catch (error: unknown) {
      const code = isApiError(error) ? error.code : UNEXPECTED_FAILURE;

      if (code === EXPIRED) {
        setExpired(true);
        return;
      }

      const failure = isApiError(error) ? parseValidationFailureDetails(error.details) : null;
      const named = FORM_FIELDS.filter((field) => failure?.fieldErrors[field] !== undefined);
      if (named.length > 0) {
        for (const field of named) {
          const policy =
            field === 'tenantName'
              ? textPolicyMessage(t, 'custom', failure?.fieldErrors[field]?.[0] ?? '')
              : null;
          form.setError(field, { type: 'server', message: policy ?? validationMessage });
        }
        return;
      }

      setErrorCode(code);
    } finally {
      // Single-use token: re-arm after every attempt, whatever happened.
      captcha.reset();
    }
  });

  if (pending.isPending) {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardContent>
            <p role="status" className="text-subtle text-sm">
              {t('federatedSignup.loading')}
            </p>
          </CardContent>
        </Card>
      </AuthCardLayout>
    );
  }

  const loadCode = pending.isError
    ? isApiError(pending.error)
      ? pending.error.code
      : UNEXPECTED_FAILURE
    : null;

  if (expired || loadCode === EXPIRED) {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-2xl font-semibold text-primary text-balance">
              {t('federatedSignup.expiredTitle')}
            </h1>
            <CardDescription role="alert">{errorMessage(t, EXPIRED)}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button asChild>
              <Link to={CENTRAL_AUTH_PAGE_PATHS.signup}>{t('federatedSignup.startAgain')}</Link>
            </Button>
          </CardFooter>
        </Card>
      </AuthCardLayout>
    );
  }

  if (loadCode !== null || pending.data === undefined) {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-2xl font-semibold text-primary text-balance">
              {t('federatedSignup.loadFailedTitle')}
            </h1>
            <CardDescription role="alert">
              {errorMessage(t, loadCode ?? UNEXPECTED_FAILURE)}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              type="button"
              onClick={() => {
                void pending.refetch();
              }}
            >
              {t('central.retry')}
            </Button>
          </CardFooter>
        </Card>
      </AuthCardLayout>
    );
  }

  const isSubmitting = form.formState.isSubmitting;
  const data = pending.data;

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-2xl font-semibold text-primary text-balance">
            {t('federatedSignup.title')}
          </h1>
          <CardDescription>
            {t('federatedSignup.description', {
              provider: t(`federatedSignup.providers.${data.provider}`),
              email: data.email,
            })}
          </CardDescription>
        </CardHeader>

        <Form {...form}>
          <form
            noValidate
            onSubmit={(event) => {
              void onSubmit(event);
            }}
          >
            <CardContent className="flex flex-col gap-3">
              {errorCode === null ? null : (
                <p role="alert" className="text-danger text-sm text-pretty">
                  {errorMessage(t, errorCode)}
                </p>
              )}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('signup.nameLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="name"
                        placeholder={t('signup.nameLabel')}
                        width="full"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tenantName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('signup.tenantNameLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="organization"
                        placeholder={t('signup.tenantNameLabel')}
                        width="full"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="sr-only">{t('signup.phoneLabel')}</FormLabel>
                    <FormControl>
                      <PhoneInput
                        name={field.name}
                        ref={field.ref}
                        onBlur={field.onBlur}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder={t('signup.phoneLabel')}
                        label={t('signup.phoneLabel')}
                        countryLabel={t('signup.phoneCountryLabel')}
                        searchLabel={t('signup.phoneSearchLabel')}
                        searchPlaceholder={t('signup.phoneSearchPlaceholder')}
                        emptyMessage={t('signup.phoneEmptyMessage')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="acceptTerms"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-start gap-2">
                      <FormControl>
                        <Checkbox
                          name={field.name}
                          ref={field.ref}
                          checked={field.value}
                          onBlur={field.onBlur}
                          onCheckedChange={(checked) => {
                            field.onChange(checked === true);
                          }}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal text-pretty">
                        <TermsAgreementText
                          message={agreement.checkboxMessage}
                          termsUrl={agreement.termsUrl}
                          privacyUrl={agreement.privacyUrl}
                        />
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <CaptchaField captcha={captcha} />
            </CardContent>

            <CardFooter className="flex flex-col items-stretch gap-2">
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || captcha.token === null}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? t('signup.submitting') : t('signup.submit')}
              </Button>
              <p className="text-subtle text-center text-xs text-pretty">{t('signup.phoneHint')}</p>
              <p className="text-subtle text-center text-sm text-pretty">
                <TextLink asChild>
                  <Link to={CENTRAL_AUTH_PAGE_PATHS.signup}>
                    {t('federatedSignup.differentAccount')}
                  </Link>
                </TextLink>
              </p>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </AuthCardLayout>
  );
}
